import React, { useEffect } from 'react';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';

interface LanguageSwitcherProps {
  darkMode?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ darkMode = false }) => {
  // Use untyped translation hook to bypass TypeScript errors
  const { t, i18n } = useUntypedTranslation();

  // Normalize the language code to match our supported languages
  const normalizeLanguage = (lang: string): string => {
    // If language includes a region (e.g., 'en-US'), extract the main language part
    const mainLang = lang.split('-')[0];
    // Check if the main language is supported, otherwise default to 'en'
    return ['en', 'de'].includes(mainLang) ? mainLang : 'en';
  };

  // Get the current normalized language
  const currentLang = normalizeLanguage(i18n.language);

  // Effect to ensure language is normalized on mount
  useEffect(() => {
    const normalizedLang = normalizeLanguage(i18n.language);
    if (normalizedLang !== i18n.language) {
      i18n.changeLanguage(normalizedLang);
    }
  }, [i18n]);

  const changeLanguage = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newLang = event.target.value as string;
    i18n.changeLanguage(newLang);
  };

  const darkStyles = darkMode
    ? {
        '& .MuiOutlinedInput-root': {
          color: 'white',
          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.6)' },
          '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.8)' },
          '& .MuiSvgIcon-root': { color: 'white' },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
          borderRadius: '22px',
        },
        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
        '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.9)' },
      }
    : {};

  return (
    <FormControl variant="outlined" size="small" sx={{ minWidth: 120, ...darkStyles }}>
      <InputLabel id="language-select-label">{t('language.select')}</InputLabel>
      <Select
        labelId="language-select-label"
        id="language-select"
        value={currentLang}
        onChange={changeLanguage as any}
        label={t('language.select')}
      >
        <MenuItem value="en">{t('language.en')}</MenuItem>
        <MenuItem value="de">{t('language.de')}</MenuItem>
      </Select>
    </FormControl>
  );
};

export default LanguageSwitcher;
